import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'helpers/no_conflict_lodash';
import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-solid-svg-icons';

import { startUpdateRecord, handleUpdateRecord, updateEditingRecord } from '../store/actions/run_records';
import { getCurrentStep } from '../store/record_table_ui_selectors';
import { getEditRow, getEditingRecord, getHeaders } from '../store/run_records_selectors';

export function RecordRow({
  currentStep,
  editingRecord,
  editRow,
  headers,
  record,
  onStartUpdateRecord,
  onUpdateEditingRecord,
  onUpdateRecord,
}) {

  const [editingField, setEditingField] = useState(null);
  const inputRef = React.createRef();

  const handleClickOutside = (e) => {
    if (inputRef && inputRef.current &&
        !inputRef.current.contains(e.target)) {
      setEditingField(null);
    }
  };

  const handleEditCell = (field) => {
    if (editableCell(field)) {
      onStartUpdateRecord(record, false);
      setEditingField(field);
    }
  };

  const throttleRecordUpdate = _.throttle(onUpdateRecord, 1000);

  const handleEditRecord = (field, value) => {
    const newHeaderFields = { ...editingRecord.headerToFields };
    newHeaderFields[field] = value;
    editingRecord.headerToFields = { ...newHeaderFields };

    onUpdateEditingRecord(editingRecord);
    if (!editRow) {
      throttleRecordUpdate(false);
    }
  };

  const editableCell = (field) => {
    return record.mappedFields.includes(field) && currentStep == 'preview';
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const renderInput = (field) => {
    return (
      <td className="table-cell p-0" key={`${editingRecord.id} + ${field}`}>
        <input
          className="h-100 w-100"
          name={`${field}-input`}
          ref={inputRef}
          type="text"
          value={editingRecord.headerToFields[field] || ''}
          onChange={e => (handleEditRecord(field, e.target.value))}
        />
      </td>
    );
  };

  const renderCell = (field) => {
    const isEditableCell = editableCell(field);

    const cellClass = classNames({
      'editable-table-cell': isEditableCell,
      'table-cell': !isEditableCell,
      'record-status': record.errorFields.includes(field)
    });

    return (
      <td
        className={cellClass}
        key={`${record.id} + ${field}`}
        onClick={() => handleEditCell(field)}
      >
        <div className="d-inline-flex w-100">
          <div className="w-100">{record.headerToFields[field]}</div>
          {isEditableCell &&
            <FontAwesomeIcon
              className="pull-right edit-icon ml-3"
              icon={faEdit}
              size="lg"
            />
          }
        </div>
      </td>
    );
  };

  const renderHeaderCells = () => {
    return headers.map((header) => {
      if ((editingField === header && record.id === editingRecord.id) ||
        (record.id === editingRecord.id && editRow)) {
        return renderInput(header);
      } else {
        return renderCell(header);
      }
    });
  };

  return (
    <tr className={`${record.previewStatus}-row`} key={record.id}>
      {currentStep === 'preview' && <td className="text-italic table-cell">{record.target}</td>}
      {renderHeaderCells()}
    </tr>
  );
}

RecordRow.propTypes = {
  currentStep: PropTypes.string.isRequired,
  editingRecord: PropTypes.object.isRequired,
  editRow: PropTypes.bool,
  headers: PropTypes.array.isRequired,
  record: PropTypes.shape({
    errorFields: PropTypes.array.isRequired,
    id: PropTypes.number.isRequired,
    index: PropTypes.number.isRequired,
    mappedFields: PropTypes.array.isRequired,
    headerToFields: PropTypes.object.isRequired,
    skip: PropTypes.bool.isRequired,
    target: PropTypes.string.isRequired
  }).isRequired,
  onStartUpdateRecord: PropTypes.func.isRequired,
  onUpdateEditingRecord: PropTypes.func.isRequired,
  onUpdateRecord: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  currentStep: getCurrentStep(state),
  editRow: getEditRow(state),
  editingRecord: getEditingRecord(state),
  headers: getHeaders(state)
});

const mapDispatchToProps = {
  onStartUpdateRecord: startUpdateRecord,
  onUpdateEditingRecord: updateEditingRecord,
  onUpdateRecord: handleUpdateRecord
};

export default connect(mapStateToProps, mapDispatchToProps)(RecordRow);
